import {
    Body1,
    Button,
    Field,
    InteractionTag,
    InteractionTagPrimary,
    InteractionTagSecondary,
    makeStyles,
    Menu,
    MenuButton,
    MenuItem,
    MenuList,
    MenuPopover,
    MenuTrigger,
    mergeClasses,
    shorthands,
    Spinner,
    Text,
    tokens,
    Tooltip,
    Title1,
} from '@fluentui/react-components';
import { ArrowUpload24Regular, AttachRegular, Dismiss24Regular, Save24Regular } from '@fluentui/react-icons';
import { DragEvent, FC, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useBeforeUnload } from 'react-router-dom';
import Skeleton from 'react-loading-skeleton';
import { appInsights } from '../../AppInsights';
import { useNotification } from '../../Contexts/NotificationContext';
import { useUserContext } from '../../Contexts/UserContext';
import { useConfirm } from '../../Contexts/hooks';
import { useIsMobile } from '../../Hooks/useResponsive';
import {
    FileParameter,
    GptAttachmentConfiguration,
    GptConfiguration,
    IGptConfiguration,
    StringStringTuple,
} from '../../EnterpriseGPTApi/EnterpriseGPTApiV1Client';
import { GptConfigurationEx } from '../../store/models/GptConfigurationEx';
import useGdriveDownload from '../../Hooks/useGdriveDownload';
import useGooglePicker from '../../Hooks/useGooglePicker';
import { performEnterpriseGPTApiCall } from '../../Hooks/useMiddlewareApiCall';
import { ExploreGptRoute } from '../../Routes';
import { cleanFileName } from '../../Utils/file';
import { addGptConfiguration, editGptConfiguration } from '../../store/actions/gptConfigurationsAction';
import { useFeature, useRootState } from '../../store/hooks';
import {
    EventName_UserInteraction,
    EventProperty_Value_ApplicationType,
    EventProperty_Value_PersonalGptUploadDocument,
    UserInteractionEventProperties,
} from '../../store/models/customEvents';
import { FileTypeIcon } from '../Common/FileTypeIcon';
import SpinnerOverlay from '../Common/SpinnerOverlay';
import {
    Settings,
    FileText,
    Check,
    ChevronDown,
    Info,
    Trash2,
    AlertTriangle,
    CheckCircle2,
    Upload,
    LayoutGrid,
} from 'lucide-react';
import spinnerGif from '../../assets/Spinner.gif';
import { HorizontalSeparatorLine } from '../Common/HorizontalSeparatorLine';
import SyButton from '../Common/SyButton';
import { SyInput } from '../Common/SyInput';
import { SyTextarea } from '../Common/SyTextarea';
import { CDialog } from '../Dialogs/CDialog';
import '../../styles/skeletons.css';

const useStyles = makeStyles({
    page: {
        width: '100%',
        margin: '0 auto',
        backgroundColor: tokens.colorNeutralBackground3,
        overflowY: 'auto',
        paddingBottom: tokens.spacingVerticalXXL,
    },
    header: {
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        paddingRight: tokens.spacingHorizontalM,
        flexShrink: 0,
        backgroundColor: tokens.colorNeutralBackground1,
        borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
        position: 'sticky',
        top: 0,
        zIndex: 10,
    },
    headerTitle: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'start',
        padding: `${tokens.spacingVerticalM} ${tokens.spacingVerticalM} ${tokens.spacingVerticalM} 17px`,
        width: '100%',
        flexWrap: 'nowrap',
        gap: tokens.spacingHorizontalS,
    },
    titleIcon: {
        fontSize: tokens.fontSizeBase500,
        color: tokens.colorNeutralForeground1,
        flexShrink: 0,
    },
    titleText: {
        flex: 1,
        minWidth: 0,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        maxWidth: '800px',
        '@media (max-width: 768px)': {
            maxWidth: '150px',
        },
    },
    content: {
        maxWidth: '900px',
        margin: '0 auto',
        ...shorthands.padding(tokens.spacingVerticalXXL, tokens.spacingHorizontalXXL),
        backgroundColor: tokens.colorNeutralBackground1,
        marginTop: tokens.spacingVerticalXXL,
        overflowY: 'auto',
        '@media (max-width: 768px)': {
            ...shorthands.padding(tokens.spacingVerticalL, tokens.spacingHorizontalM),
            marginTop: tokens.spacingVerticalL,
        },
        '& h1, & h2, & h3, & h4, & h5, & h6': {
            lineHeight: '1.2',
            color: 'var(--heading-text-color)',
            fontWeight: tokens.fontWeightSemibold,
        },
        '& h1': {
            fontSize: '22px',
        },
        '& h2': {
            fontSize: '18px',
        },
        '& h3': {
            fontSize: '16px',
        },
        '& .label': {
            fontSize: tokens.fontSizeBase300,
            fontWeight: tokens.fontWeightMedium,
            color: 'var(--heading-text-color)',
        },
    },
    section: {
        marginBottom: tokens.spacingVerticalXXXL,
    },
    sectionHeader: {
        display: 'flex',
        flexDirection: 'column',
        gap: tokens.spacingVerticalXS,
        alignItems: 'start',
        marginTop: '-15px',
        marginBottom: tokens.spacingVerticalS,
    },
    sectionHeaderTitle: {
        display: 'flex',
        alignItems: 'center',
        gap: tokens.spacingHorizontalM,
    },
    sectionIcon: {
        fontSize: '20px',
        color: tokens.colorNeutralForeground1,
    },
    formField: {
        display: 'flex',
        flexDirection: 'column',
        gap: tokens.spacingVerticalS,
    },
    form: {
        display: 'flex',
        flexDirection: 'column',
        gap: tokens.spacingVerticalXXL,
    },
    uploadDescription: {
        marginTop: '-14px',
        marginBottom: '15px',
        color: tokens.colorNeutralForeground2,
    },
    uploadArea: {
        position: 'relative',
        border: `2px dashed ${tokens.colorNeutralStroke2}`,
        borderRadius: tokens.borderRadiusXLarge,
        padding: `${tokens.spacingVerticalXXXL} ${tokens.spacingHorizontalXXXL} 14px ${tokens.spacingHorizontalXXXL}`,
        textAlign: 'center',
        transition: 'all 0.2s ease',
        cursor: 'pointer',
        backgroundColor: 'transparent',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        '&:hover': {
            backgroundColor: tokens.colorNeutralBackground3,
            border: `2px dashed ${tokens.colorPaletteDarkOrangeForeground1}`,
        },
        '&:focus-visible': {
            outline: 'none',
            border: `2px dashed ${tokens.colorPaletteDarkOrangeForeground1}`,
        },
        '&:focus-within': {
            border: `2px dashed ${tokens.colorPaletteDarkOrangeForeground1}`,
            outline: 'none',
        },
        '@media (max-width: 768px)': {
            ...shorthands.padding(tokens.spacingVerticalXL),
        },
    },
    uploadAreaDragOver: {
        backgroundColor: tokens.colorBrandBackground2,
        border: `2px dashed ${tokens.colorPaletteDarkOrangeForeground1} !important`,
    },
    fileInput: {
        position: 'absolute',
        opacity: 0,
        width: '100%',
        height: '100%',
        top: 0,
        left: 0,
        cursor: 'pointer',
    },
    uploadContent: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        pointerEvents: 'auto',
    },
    uploadText: {
        fontSize: tokens.fontSizeBase400,
        color: tokens.colorNeutralForeground1,
        marginBottom: '0px',
        marginTop: tokens.spacingVerticalL,
    },
    uploadLink: {
        color: tokens.colorBrandForeground1,
        textDecoration: 'none',
        fontWeight: tokens.fontWeightMedium,
        '&:hover': {
            textDecoration: 'none',
        },
    },
    uploadFormats: {
        fontSize: tokens.fontSizeBase200,
        color: tokens.colorNeutralForeground3,
        marginTop: '8px',
    },
    statusSection: {
        marginTop: tokens.spacingVerticalL,
        borderRadius: tokens.borderRadiusLarge,
        ...shorthands.padding(tokens.spacingVerticalL),
        border: `1px solid ${tokens.colorNeutralStroke2}`,
    },
    statusHeader: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: tokens.spacingVerticalM,
        '@media (max-width: 768px)': {
            flexDirection: 'column',
            alignItems: 'flex-start',
            gap: tokens.spacingVerticalS,
        },
    },
    statusHeaderLeft: {
        display: 'flex',
        alignItems: 'center',
        gap: tokens.spacingHorizontalS,
        fontSize: tokens.fontSizeBase400,
        fontWeight: tokens.fontWeightMedium,
        color: 'var(--heading-text-color)',
        '@media (max-width: 768px)': {
            width: '100%',
            flexWrap: 'wrap',
        },
    },
    statusHeaderRight: {
        display: 'flex',
        alignItems: 'center',
        gap: tokens.spacingHorizontalS,
        '@media (max-width: 768px)': {
            width: '100%',
            justifyContent: 'flex-start',
            flexWrap: 'wrap',
        },
    },

    statusIcon: {
        width: '20px',
        height: '20px',
    },
    statusIconProcessing: {
        color: '#6366f1',
    },
    statusIconSuccess: {
        color: '#22C55E',
    },
    statusIconWarning: {
        color: '#F59E0B',
    },
    statusIconDanger: {
        color: 'red',
    },

    fileSuccessIcon: {
        color: '#22C55E',
        backgroundColor: tokens.colorPaletteGreenBackground1,
    },
    fileWarningIcon: {
        color: '#F59E0B',
        backgroundColor: '#FFFBEB',
    },
    fileDangerIcon: {
        color: 'red',
        backgroundColor: '#FFFBEB',
    },

    statusFiles: {
        display: 'flex',
        flexDirection: 'column',
        gap: 0,
        marginTop: tokens.spacingVerticalM,
    },
    statusFilesCollapsed: {
        display: 'none',
    },
    fileItem: {
        display: 'flex',
        alignItems: 'center',
        gap: tokens.spacingHorizontalM,
        ...shorthands.padding(tokens.spacingVerticalS, tokens.spacingHorizontalXXL),
        fontSize: tokens.fontSizeBase300,
        color: tokens.colorNeutralForeground1,
        position: 'relative',
        borderRadius: tokens.borderRadiusMedium,
        transition: 'all 0.2s ease',
        '&:hover': {
            backgroundColor: tokens.colorNeutralBackground3,
        },
        '&:hover button': {
            opacity: 1,
        },
        '@media (max-width: 768px)': {
            ...shorthands.padding(tokens.spacingVerticalS, tokens.spacingHorizontalM),
            gap: tokens.spacingHorizontalS,
        },
    },
    fileItemIcon: {
        width: '24px',
        height: '24px',
        textAlign: 'center',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        borderRadius: '50%',
    },

    fileItemName: {
        flex: 1,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        fontSize: tokens.fontSizeBase300,
        fontWeight: tokens.fontWeightRegular,
    },
    fileDeleteBtn: {
        background: 'none',
        border: 'none',
        color: tokens.colorNeutralForeground3,
        cursor: 'pointer',
        fontSize: tokens.fontSizeBase300,
        ...shorthands.padding(tokens.spacingVerticalXS),
        transition: 'color 0.2s ease, opacity 0.2s ease',
        opacity: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '32px',
        height: '32px',
        borderRadius: tokens.borderRadiusMedium,
        '&:hover': {
            color: tokens.colorNeutralForeground2,
            backgroundColor: tokens.colorNeutralBackground1,
        },
        '@media (max-width: 768px)': {
            opacity: 1,
        },
    },
    fileItemHover: {
        '&:hover button': {
            opacity: 1,
        },
    },
    spinner: {
        width: '20px',
        height: '20px',
        flexShrink: 0,
    },
    buttonsContainer: {
        display: 'flex',
        flexDirection: 'row-reverse',
        gap: tokens.spacingHorizontalM,
        '@media (max-width: 768px)': {
            flexDirection: 'column-reverse',
            gap: tokens.spacingVerticalM,
        },
    },
    textBtn: {
        background: 'none',
        border: 'none',
        color: tokens.colorBrandForeground1,
        cursor: 'pointer',
        fontSize: tokens.fontSizeBase300,
        fontWeight: tokens.fontWeightMedium,
        ...shorthands.padding(tokens.spacingVerticalXS, tokens.spacingHorizontalS),
        transition: 'color 0.2s ease',
        '&:hover': {
            color: tokens.colorBrandForeground2,
        },
    },
    iconBtn: {
        background: 'none',
        border: 'none',
        color: tokens.colorNeutralForeground3,
        cursor: 'pointer',
        ...shorthands.padding(tokens.spacingVerticalXS),
        transition: 'color 0.2s ease',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        '&:hover': {
            color: tokens.colorNeutralForeground2,
        },
    },
    uploadIconContainer: {
        display: 'flex',
        flexDirection: 'row',
        height: '35px',
        alignItems: 'center',
        ...shorthands.gap('10px'),
    },
    dropOverlay: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'rgba(255, 255, 255, 0.8)',
        borderRadius: '4px',
        zIndex: 10,
    },
    dropMessage: {
        fontSize: '18px',
        fontWeight: 'bold',
        color: tokens.colorBrandForeground1,
        padding: '20px',
        textAlign: 'center',
        borderRadius: '8px',
        backgroundColor: 'rgba(255, 255, 255, 0.9)',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
        '@media (max-width: 768px)': {
            fontSize: '16px',
            padding: '15px',
        },
    },
    knowledgeContainer: {
        marginTop: '10px',
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        ...shorthands.gap('10px'),
        position: 'relative',
        padding: '10px 20px 20px 20px',
        border: '2px dashed transparent',
        borderRadius: '4px',
        transition: 'all 0.2s ease',
        '@media (max-width: 768px)': {
            padding: '10px 15px 15px 15px',
            ...shorthands.gap('8px'),
        },
    },
    draggingOver: {
        border: '2px dashed #ff9966 !important',
        backgroundColor: tokens.colorBrandBackground2,
    },
    partialFileProcessingTooltip: {
        textAlign: 'left',
        paddingLeft: '20px',
        marginLeft: 0,
        '& li': {
            textAlign: 'left',
            listStylePosition: 'outside',
        },
    },
});

const defaultPersonalGpt: IGptConfiguration = {
    id: '',
    name: '',
    description: '',
    prompt: '',
};

const UploadIcon: FC = () => (
    <svg width='70' height='51' viewBox='0 0 70 51' fill='none' xmlns='http://www.w3.org/2000/svg'>
        <path
            d='M6.05172 8.74549L17.2131 6.88525V40.7377L12.3018 41.7717C9.01306 42.464 5.79705 40.3203 5.17081 37.0184L1.14319 15.7819C0.515988 12.4748 2.73148 9.29886 6.05172 8.74549Z'
            stroke='#FF5730'
            strokeWidth='2'
        />
        <path
            d='M63.9483 8.74549L52.7869 6.88525V40.7377L57.6982 41.7717C60.9869 42.464 64.203 40.3203 64.8292 37.0184L68.8568 15.7819C69.484 12.4748 67.2685 9.29886 63.9483 8.74549Z'
            stroke='#FF5730'
            strokeWidth='2'
        />
        <g filter='url(#filter0_d_3271_14046)'>
            <rect
                x='17.0654'
                y='1'
                width='35.8689'
                height='42.7541'
                rx='5'
                stroke='#FF5730'
                strokeWidth='2'
                shapeRendering='crispEdges'
            />
        </g>
        <path
            d='M38.9824 33.0894C39.7831 34.0105 41.215 34.0058 42.0098 33.0796L47.2451 26.9761L52.9346 33.0981V38.7544C52.9344 41.5156 50.6958 43.7542 47.9346 43.7544H22.0654C19.3041 43.7544 17.0656 41.5157 17.0654 38.7544V35.2935L29.4727 22.145L38.9824 33.0894Z'
            stroke='#FF5730'
            strokeWidth='2'
        />
        <circle cx='39.5902' cy='14.3442' r='4.16393' stroke='#FF5730' strokeWidth='2' />
        <defs>
            <filter
                id='filter0_d_3271_14046'
                x='13.0654'
                y='0'
                width='43.8687'
                height='50.7542'
                filterUnits='userSpaceOnUse'
                colorInterpolationFilters='sRGB'
            >
                <feFlood floodOpacity='0' result='BackgroundImageFix' />
                <feColorMatrix
                    in='SourceAlpha'
                    type='matrix'
                    values='0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0'
                    result='hardAlpha'
                />
                <feOffset dy='3' />
                <feGaussianBlur stdDeviation='1.5' />
                <feComposite in2='hardAlpha' operator='out' />
                <feColorMatrix type='matrix' values='0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.12 0' />
                <feBlend mode='normal' in2='BackgroundImageFix' result='effect1_dropShadow_3271_14046' />
                <feBlend mode='normal' in='SourceGraphic' in2='effect1_dropShadow_3271_14046' result='shape' />
            </filter>
        </defs>
    </svg>
);

const ConfigureGptView: FC = () => {
    const styles = useStyles();
    const { t } = useTranslation();
    const { dispatchNotification } = useNotification();
    const { dispatch } = useRootState();
    const navigate = useNavigate();
    const { userProfile } = useUserContext();
    const confirm = useConfirm();
    const chatFeature = useFeature('chat');
    const isMobile = useIsMobile();

    // Helper function to apply attachment configuration fallback for personal GPTs
    const applyAttachmentConfigFallback = useCallback(
        (gpt: IGptConfiguration): GptConfigurationEx => {
            const gptEx = new GptConfigurationEx(gpt);
            gptEx.isShared = false; // Personal GPTs are not shared

            if (gpt.isPersonal && !gpt.attachment && chatFeature.personalGpt?.attachmentConfiguration) {
                gptEx.attachment = new GptAttachmentConfiguration(chatFeature.personalGpt.attachmentConfiguration);
            }
            return gptEx;
        },
        [chatFeature],
    );

    const supportedFileExtensions = chatFeature.personalGpt?.knowledgeBase?.extensions;
    const maxSupportedFileSizeMB = chatFeature.personalGpt?.knowledgeBase?.maxSizeMB;
    const maxSupportedTotalFilesSizeMB = chatFeature.personalGpt?.knowledgeBase?.maxTotalFilesSizeMB;
    const hiddenFileInput = useRef(null as HTMLInputElement | null);
    const [initialFiles, setInitialFiles] = useState<string[]>([]);
    const [fileNames, setFileNames] = useState<string[]>([]);
    const [filesToUpsert, setFilesToUpsert] = useState<FileParameter[]>([]);
    const [filesToDelete, setFilesToDelete] = useState<string[]>([]);
    const [filesStatus, setFilesStatus] = useState<StringStringTuple[]>([]);
    const [showPartialFiles, setShowPartialFiles] = useState<boolean>(true);
    const [showProcessingFiles, setShowProcessingFiles] = useState<boolean>(true);
    const [showSuccessFiles, setShowSuccessFiles] = useState<boolean>(true);
    const [showFailedFiles, setShowFailedFiles] = useState<boolean>(true);
    const [showUploadedFiles, setShowUploadedFiles] = useState<boolean>(true);
    const [personalGpt, setPersonalGpt] = useState<IGptConfiguration | undefined>(undefined);
    const [mode, setMode] = useState<'create' | 'edit'>('create');
    const [isLoading, setIsLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [isFormFilled, setIsFormFilled] = useState(false);
    const [isDriveFileDownloading, setIsDriveFileDownloading] = useState<boolean>(false);
    const [maxFilesReached, setMaxFilesReached] = useState(false);
    const [isDraggingOver, setIsDraggingOver] = useState(false);
    const { id: personalGptId } = useParams();

    const { getFilesByIds } = useGdriveDownload(confirm, chatFeature.personalGpt?.knowledgeBase?.extensions!);

    const onUploadDocumentAddRef = useRef<(files: FileList) => void>();

    // Log file names and statuses to the console whenever filesStatus changes
    useEffect(() => {
        if (filesStatus && filesStatus.length > 0) {
            console.group && console.group('ConfigureGptView - filesStatus');
            filesStatus.forEach((fs) => {
                try {
                    const name = fs?.item1 ?? '<unknown>';
                    const status = fs?.item2 ?? '<unknown>';
                    console.log(`File: ${name} — Status: ${status}`);
                } catch (e) {
                    console.log('ConfigureGptView - filesStatus item malformed', fs);
                }
            });
            console.groupEnd && console.groupEnd();
        } else {
            console.log('ConfigureGptView - filesStatus is empty');
        }
    }, [filesStatus]);

    // Handle Google Picker Callback
    const onPickerCallback = async (data: any): Promise<void> => {
        if (data.action === window.google.picker.Action.PICKED) {
            const fileIds = (data.docs as any[]).map((x) => x.id);
            const fileNames = (data.docs as any[]).map((x) => x.name);

            setIsDriveFileDownloading(true);
            const files: FileList | null = await getFilesByIds(fileIds, fileNames);
            setIsDriveFileDownloading(false);

            if (files) {
                onUploadDocumentAddRef.current!(files);
            }
        }
    };

    const { openPicker } = useGooglePicker(
        onPickerCallback,
        chatFeature.personalGpt?.knowledgeBase?.extensions?.map((x) => x.mimeType).join(',')!,
    );

    // State to store the initial version of the GPT for dirty checking in edit mode
    const [initialPersonalGpt, setInitialPersonalGpt] = useState<IGptConfiguration | undefined>(undefined);
    const [initialFilesToDelete, setInitialFilesToDelete] = useState<string[]>([]);

    useEffect(() => {
        if (personalGptId) {
            setMode('edit');
            setIsLoading(true);
            performEnterpriseGPTApiCall(async (client) => {
                try {
                    const response = await client.getPersonalGpt(personalGptId);
                    const personalGpt = new GptConfiguration();
                    personalGpt.id = response.id;
                    personalGpt.name = response.name;
                    personalGpt.description = response.description;
                    personalGpt.prompt = response.instruction;
                    setPersonalGpt(personalGpt);
                    setInitialPersonalGpt({ ...personalGpt }); // Store snapshot for dirty checking
                    setInitialFiles(response.files ?? []);
                    setFileNames(response.files ?? []);
                    setFilesStatus(response.filesStatus ?? []);
                } catch (e) {
                    dispatchNotification('error.api.post.personalGpt', 'error');
                } finally {
                    setIsLoading(false);
                }
            });
        } else {
            setPersonalGpt(defaultPersonalGpt);
            setInitialPersonalGpt(undefined); // No initial state for create mode
            setIsLoading(false);
        }
    }, []);

    // Validation: Check if all required fields are filled
    // Required fields: Name of GPT, Description of the GPT, and Instructions
    const hasRequiredFieldsFilled =
        personalGpt?.name?.trim() && personalGpt?.description?.trim() && personalGpt?.prompt?.trim() ? true : false;

    useEffect(() => {
        setIsFormFilled(hasRequiredFieldsFilled);
    }, [personalGpt, hasRequiredFieldsFilled]);

    // Track unsaved changes
    const [showConfirmDialog, setShowConfirmDialog] = useState(false);
    const navigationAttemptRef = useRef<'close' | null>(null);

    // Compute isDirty by comparing current state to initial state
    const isDirty = useCallback(() => {
        if (!personalGpt || isLoading) return false;
        
        if (mode === 'create') {
            // For create mode, check if any fields are filled
            return !!(
                personalGpt.name?.trim() ||
                personalGpt.description?.trim() ||
                personalGpt.prompt?.trim() ||
                filesToUpsert.length > 0
            );
        } else {
            // For edit mode, check if anything has changed from the initial loaded state
            return !!(
                (personalGpt.name?.trim() !== initialPersonalGpt?.name?.trim()) ||
                (personalGpt.description?.trim() !== initialPersonalGpt?.description?.trim()) ||
                (personalGpt.prompt?.trim() !== initialPersonalGpt?.prompt?.trim()) ||
                filesToUpsert.length > 0 ||
                filesToDelete.length > 0
            );
        }
    }, [personalGpt, initialPersonalGpt, filesToUpsert, filesToDelete, mode, isLoading]);

    const handleConfirm = () => {
        setShowConfirmDialog(false);
        navigationAttemptRef.current = null;
        navigate(ExploreGptRoute);
    };

    const handleCancel = () => {
        setShowConfirmDialog(false);
        navigationAttemptRef.current = null;
    };

    // Prevent navigation if there are unsaved changes
    const onClose = useCallback(() => {
        if (isDirty()) {
            navigationAttemptRef.current = 'close';
            setShowConfirmDialog(true);
        } else {
            navigate(ExploreGptRoute);
        }
    }, [isDirty, navigate]);

    // Handle browser back button and page close
    useBeforeUnload(
        useCallback(
            (event) => {
                if (isDirty()) {
                    event.preventDefault();
                    event.returnValue = '';
                }
            },
            [isDirty]
        )
    );

    const onUploadFromLocalDriveClick = useCallback(() => {
        if (hiddenFileInput.current) {
            hiddenFileInput.current.click();
            hiddenFileInput.current.value = '';
        }
    }, []);

    const onUploadFromGoogleDriveClick = useCallback(() => {
        const expiresBy = localStorage.getItem('expiresBy');
        if (localStorage.getItem('pc_t') && expiresBy) {
            if (new Date().getTime() >= new Date(expiresBy).getTime()) {
                localStorage.removeItem('pc_t');
                localStorage.removeItem('expiresBy');
            }
        }
        openPicker();
    }, []);

    const onCreatePersonalGpt = useCallback(() => {
        performEnterpriseGPTApiCall(async (client, abortSignal) => {
            try {
                setIsLoading(true);
                setSaving(true);
                const personalGptResult = await client.createPersonalGpt(
                    personalGpt?.name,
                    personalGpt?.description,
                    personalGpt?.prompt,
                    [], // starters
                    filesToUpsert,
                    [], // filesToDelete
                    [], // sharedWith
                    false, // isPublic
                    false, // duplicable
                    abortSignal,
                );
                setIsLoading(false);
                setSaving(false);
                dispatch(addGptConfiguration(applyAttachmentConfigFallback(personalGptResult)));
                navigate(ExploreGptRoute);
                dispatchNotification('personalGpt.actions.create', 'success');
            } catch (e) {
                setIsLoading(false);
                setSaving(false);
                dispatchNotification('error.api.post.personalGpt', 'error');
            }
        });
    }, [personalGpt, filesToUpsert, dispatch, navigate, dispatchNotification, applyAttachmentConfigFallback]);

    const onEditPersonalGpt = useCallback(() => {
        performEnterpriseGPTApiCall(async (client, abortSignal) => {
            try {
                setIsLoading(true);
                setSaving(true);

                // First, get the latest version from the server to preserve sharedWith and other properties
                const currentGptData = await client.getPersonalGpt(personalGpt?.id!, abortSignal);

                const personalGptResult = await client.modifyPersonalGpt(
                    personalGpt?.id!,
                    personalGpt?.name,
                    personalGpt?.description,
                    personalGpt?.prompt,
                    [], // starters
                    filesToUpsert,
                    filesToDelete,
                    currentGptData.sharedWith || [], // sharedWith - preserve existing from server
                    currentGptData.isPublic || false, // isPublic - preserve existing from server
                    currentGptData.isDuplicable || false, // duplicable - preserve existing from server
                    abortSignal,
                );
                setIsLoading(false);
                setSaving(false);
                dispatch(editGptConfiguration(applyAttachmentConfigFallback(personalGptResult)));
                navigate(ExploreGptRoute);
                dispatchNotification('personalGpt.actions.edit', 'success');
            } catch (e) {
                setIsLoading(false);
                setSaving(false);
                dispatchNotification('error.api.put.personalGpt', 'error');
            }
        });
    }, [personalGpt, filesToUpsert, filesToDelete, dispatch, navigate, dispatchNotification, applyAttachmentConfigFallback]);

    const onUploadDocumentAdd = useCallback(
        (files: FileList) => {
            var customEvent = {
                name: EventName_UserInteraction,
                properties: {
                    applicationType: EventProperty_Value_ApplicationType,
                    userId: userProfile?.username,
                    interactionType: EventProperty_Value_PersonalGptUploadDocument,
                } as UserInteractionEventProperties,
            };

            if (fileNames.length + files.length > chatFeature.personalGpt?.knowledgeBase?.maxFiles!) {
                setMaxFilesReached(true);
                customEvent.properties.isSuccess = false;
                customEvent.properties.error = 'maxFilesReached';
                appInsights.trackEvent(customEvent);

                confirm({
                    show: true,
                    title: t(`application.dialog.warningTitle`),
                    message: `${t(`personalGpt.knowledgeBase.maxFilesReached`)} ${chatFeature.personalGpt?.knowledgeBase?.maxFiles}.`,
                    confirmText: t('Ok'),
                });
                return;
            }

            const existingTotalSize = filesToUpsert.reduce((total, doc) => total + doc.data.size / 1000000, 0);
            const newFilesTotalSize = Array.from(files).reduce((total, file) => total + file.size / 1000000, 0);

            if (maxSupportedTotalFilesSizeMB && existingTotalSize + newFilesTotalSize > maxSupportedTotalFilesSizeMB) {
                customEvent.properties.isSuccess = false;
                customEvent.properties.error = 'totalFilesTooLarge';
                appInsights.trackEvent(customEvent);

                confirm({
                    show: true,
                    title: t(`application.dialog.warningTitle`),
                    message: `${t(`personalGpt.knowledgeBase.totalFilesTooLarge`)} ${maxSupportedTotalFilesSizeMB} MB`,
                    confirmText: t('Ok'),
                });
                return;
            }

            const newFilesToUpsert: FileParameter[] = [...filesToUpsert];
            const newFileNames: string[] = [...fileNames];
            let validFiles = 0;

            for (let i = 0; i < files.length; i++) {
                const file = files[i];

                if (
                    supportedFileExtensions &&
                    !supportedFileExtensions?.some((ext) => file.name.toLowerCase().endsWith(ext.value!))
                ) {
                    continue;
                }

                if (file.size == 0) {
                    continue;
                }

                if (maxSupportedFileSizeMB && file.size / 1000000 > maxSupportedFileSizeMB) {
                    continue;
                }

                var cleanedFileName = cleanFileName(file.name);

                const fileToUpsertIndex = newFilesToUpsert.findIndex((x) => x.fileName === cleanedFileName);
                if (fileToUpsertIndex !== -1) {
                    newFilesToUpsert.splice(fileToUpsertIndex, 1);
                }

                newFilesToUpsert.push({ fileName: cleanedFileName, data: file });

                const fileNameIndex = newFileNames.indexOf(cleanedFileName);
                if (fileNameIndex !== -1) {
                    newFileNames.splice(fileNameIndex, 1);
                }

                newFileNames.push(cleanedFileName);
                validFiles++;
            }

            if (validFiles > 0) {
                setFilesToUpsert(newFilesToUpsert);
                setFileNames(newFileNames);

                customEvent.properties.isSuccess = true;
                appInsights.trackEvent(customEvent);
            }
        },
        [fileNames, filesToUpsert],
    );

    useEffect(() => {
        onUploadDocumentAddRef.current = onUploadDocumentAdd;
    }, [onUploadDocumentAdd]);

    const onUploadDocumentRemove = useCallback(
        (fileToRemove: string) => {
            setFileNames((f) => f.filter((file) => file !== fileToRemove));
            setFilesToUpsert((f) => f.filter((file) => file.fileName !== fileToRemove));
            // Also remove any status entries for this file so the UI line disappears immediately
            setFilesStatus((f) => f.filter((fs) => fs.item1 !== fileToRemove));
            if (initialFiles.some((x) => x === fileToRemove)) {
                setFilesToDelete((f) => [...f, fileToRemove]);
            }
        },
        [initialFiles],
    );

    const handleDragOver = useCallback((event: DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        event.stopPropagation();
        setIsDraggingOver(true);
    }, []);

    const handleDragLeave = useCallback((event: DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        event.stopPropagation();

        if (event.currentTarget.contains(event.relatedTarget as Node)) {
            return;
        }

        setIsDraggingOver(false);
    }, []);

    const handleDrop = useCallback(
        (event: DragEvent<HTMLDivElement>) => {
            event.preventDefault();
            event.stopPropagation();
            setIsDraggingOver(false);

            if (event.dataTransfer.files && event.dataTransfer.files.length > 0 && !maxFilesReached && !isLoading) {
                onUploadDocumentAdd(event.dataTransfer.files);
            }
        },
        [onUploadDocumentAdd, maxFilesReached, isLoading],
    );

    // Separate files into categories
    const processingFiles = filesStatus.filter((fs) => fs.item2 === 'Processing');
    const partialFiles = filesStatus.filter((fs) => fs.item2 === 'Partial');
    const failedFiles = filesStatus.filter((fs) => fs.item2 === 'Failed');

    const completeFiles = filesStatus.filter(
        (fs) => fs.item2 !== 'Partial' && fs.item2 !== 'Processing' && fs.item2 !== 'Failed',
    );

    // Files in filesStatus set
    const filesInStatus = new Set(filesStatus.map((fs) => fs.item1));
    
    // Uploaded files: files that are in fileNames but not yet saved (only in filesToUpsert)
    const uploadedFileNames = new Set(filesToUpsert.map((f) => f.fileName));
    const uploadedFiles = fileNames.filter((name) => uploadedFileNames.has(name) && !filesInStatus.has(name));
    
    // Processing files: files in filesStatus with 'Processing' status, but NOT in the uploaded-only list
    const filesNotInStatusAndNotUploaded = fileNames.filter((name) => !filesInStatus.has(name) && !uploadedFileNames.has(name));
    const allProcessingFiles = [
        ...processingFiles,
        ...filesNotInStatusAndNotUploaded.map((name) => ({ item1: name, item2: 'Processing' }) as StringStringTuple),
    ];

    // Show skeleton loading while data is being fetched
    if (isLoading && !saving) {
        return (
            <div className={styles.page}>
                {/* Header skeleton */}
                <div className='titleSection'>
                    <div className={styles.headerTitle}>
                        <div className="skeleton-rect-wrapper">
                            <Skeleton width={isMobile ? 150 : 200} height={34} />
                        </div>
                    </div>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginRight: 12 }}>
                        <div className="skeleton-rect-wrapper">
                            <Skeleton width={100} height={36} />
                        </div>
                        <div className="skeleton-rect-wrapper">
                            <Skeleton className="skeleton-btn" />
                        </div>
                    </div>
                </div>

                <HorizontalSeparatorLine />

                <div className={styles.content}>
                    {/* GPT Details Section skeleton */}
                    <section className={styles.section}>
                        <div className={styles.sectionHeader}>
                            <div className="skeleton-rect-wrapper" style={{ marginBottom: '12px' }}>
                                <Skeleton height={28} width={isMobile ? '180px' : '200px'} />
                            </div>
                        </div>
                        
                        <div className={styles.form}>
                            {/* Name field skeleton */}
                            <div className={styles.formField} style={{ marginBottom: '24px' }}>
                                <Skeleton height={20} width="120px" style={{ marginBottom: '8px' }} />
                                <Skeleton height={40} width="100%" />
                            </div>

                            {/* Description field skeleton */}
                            <div className={styles.formField} style={{ marginBottom: '24px' }}>
                                <Skeleton height={20} width="150px" style={{ marginBottom: '8px' }} />
                                <Skeleton height={80} width="100%" />
                            </div>

                            {/* Instructions field skeleton */}
                            <div className={styles.formField}>
                                <Skeleton height={20} width="180px" style={{ marginBottom: '8px' }} />
                                <Skeleton height={120} width="100%" />
                            </div>
                        </div>
                    </section>

                    {/* Knowledge Base Section skeleton */}
                    <section className={styles.section} style={{ marginTop: '32px' }}>
                        <div className={styles.sectionHeader}>
                            <div className="skeleton-rect-wrapper" style={{ marginBottom: '12px' }}>
                                <Skeleton height={28} width={isMobile ? '200px' : '240px'} />
                            </div>
                            <div className="skeleton-rect-wrapper">
                                <Skeleton height={18} width={isMobile ? '250px' : '350px'} />
                            </div>
                        </div>
                        
                        {/* Upload area skeleton */}
                        <div style={{ marginTop: '16px' }}>
                            <Skeleton height={isMobile ? 180 : 200} width="100%" />
                        </div>
                    </section>
                </div>
            </div>
        );
    }

    return (
        <div className={styles.page}>
            {/* Header */}
            <div className='titleSection'>
                <div className={styles.headerTitle}>
                    <LayoutGrid size={20} className={styles.titleIcon} />
                    <Title1 className={`settingsTitle ${styles.titleText}`}>
                        {personalGpt?.name || t('Configure GPT')}
                    </Title1>
                </div>

                <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginRight: 12 }}>
                    <SyButton
                        appearance='primary'
                        onClick={mode === 'edit' ? onEditPersonalGpt : onCreatePersonalGpt}
                        disabled={isLoading || saving || !hasRequiredFieldsFilled}
                        style={{ minWidth: 100 }}
                        icon={<Check />}
                    >
                        {saving ? t('saving', 'Saving...') : t('save', 'Save')}
                    </SyButton>
                    <SyButton
                        appearance='transparent'
                        onClick={onClose}
                        icon={<Dismiss24Regular />}
                        aria-label='Close'
                        title='Close'
                    />
                </div>
            </div>

            <HorizontalSeparatorLine />

            {personalGpt && (
                <div className={styles.content}>
                    {/* GPT Details Section */}
                    <section className={styles.section}>
                        <div className={styles.sectionHeader}>
                            <div className={styles.sectionHeaderTitle}>
                                <Settings className={styles.sectionIcon} />
                                <h2 className='h3'>{t('GPT details')}</h2>
                            </div>
                        </div>

                        <div className={styles.form}>
                            {/* Name Field */}
                            <div className={styles.formField}>
                                <Field
                                    label={
                                        <>
                                            <span className='label'>{t('Name your GPT')}</span>
                                            <span style={{ color: tokens.colorPaletteRedForeground1 }}>*</span>
                                        </>
                                    }
                                    size='large'
                                >
                                    <SyInput
                                        className='input'
                                        placeholder={t('Enter GPT name')}
                                        onChange={(_event, data) => setPersonalGpt((p) => ({ ...p, name: data.value }))}
                                        value={personalGpt?.name}
                                    />
                                </Field>
                            </div>

                            {/* Description Field */}
                            <div className={styles.formField}>
                                <Field
                                    label={
                                        <>
                                            <span className='label'>{t('Description of the GPT')}</span>
                                            <span style={{ color: tokens.colorPaletteRedForeground1 }}>*</span>
                                        </>
                                    }
                                    size='large'
                                >
                                    <SyTextarea
                                        className='textarea textarea-base'
                                        rows={3}
                                        placeholder={t('Describe what this GPT does and how it can help users...')}
                                        onChange={(_event, data) => setPersonalGpt((p) => ({ ...p, description: data.value }))}
                                        size='large'
                                        value={personalGpt?.description}
                                    />
                                </Field>
                            </div>

                            {/* Instructions Field */}
                            <div className={styles.formField}>
                                <Field
                                    label={
                                        <>
                                            <span className='label'>{t('Instructions')}</span>
                                            <span style={{ color: tokens.colorPaletteRedForeground1 }}>*</span>
                                        </>
                                    }
                                    size='large'
                                >
                                    <SyTextarea
                                        className='textarea textarea-base'
                                        rows={3}
                                        placeholder={t(
                                            'Provide detailed instructions on how the chatbot should behave, what tone to use, how to respond to different types of questions, and any specific guidelines...',
                                        )}
                                        onChange={(_event, data) => setPersonalGpt((p) => ({ ...p, prompt: data.value }))}
                                        size='large'
                                        value={personalGpt?.prompt}
                                    />
                                </Field>
                            </div>
                        </div>
                    </section>

                    {/* Knowledge Management Section */}
                    {chatFeature.personalGpt?.knowledgeBase?.isEnabled && (
                        <section className={styles.section}>
                            <div className={styles.sectionHeader}>
                                <div className={styles.sectionHeaderTitle}>
                                    <FileText className={styles.sectionIcon} size={20} />
                                    <h2 className='h3'>{t('Knowledge management')}</h2>
                                </div>
                                <div className={styles.uploadDescription}>
                                    {t(
                                        'Upload up to 20 files that the chatbot can use to provide more accurate and context-aware answers.',
                                    )}
                                </div>
                            </div>

                            {/* File Upload Area */}
                            <div
                                className={`${styles.uploadArea} ${isDraggingOver ? styles.uploadAreaDragOver : ''}`}
                                onDragOver={handleDragOver}
                                onDragLeave={handleDragLeave}
                                onDrop={handleDrop}
                                onClick={(e) => {
                                    // Prevent clicks on the upload area from opening the file dialog.
                                    // Only allow the explicit 'from your device' link to trigger file input.
                                    if (e.target === e.currentTarget) {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        return;
                                    }
                                }}
                            >
                                <input
                                    type='file'
                                    multiple
                                    accept={supportedFileExtensions?.map((x) => x.value).join(', ')}
                                    ref={hiddenFileInput}
                                    style={{ display: 'none' }}
                                    onChange={(event) => onUploadDocumentAdd(event.target.files!)}
                                />
                                <div className={styles.uploadContent}>
                                    <UploadIcon />
                                    <p className={`paragraph ${styles.uploadText}`}>
                                        {t('Drop your files here or browse ')}
                                        <a
                                            href='#'
                                            className={styles.uploadLink}
                                            onClick={(e) => {
                                                e.preventDefault();
                                                onUploadFromLocalDriveClick();
                                            }}
                                        >
                                            {t('from your device')}
                                        </a>
                                        {t(' or ')}
                                        <a
                                            href='#'
                                            className={styles.uploadLink}
                                            onClick={(e) => {
                                                e.preventDefault();
                                                onUploadFromGoogleDriveClick();
                                            }}
                                        >
                                            {t('google drive')}
                                        </a>
                                    </p>
                                    <p className={`paragraph-xs ${styles.uploadFormats}`}>
                                        {t('Supported formats: DOCX, PDF, TXT, PPT (Max 50MB per file)')}
                                    </p>
                                </div>
                                {isDraggingOver && (
                                    <div className={styles.dropOverlay}>
                                        <div className={styles.dropMessage}>{t('Drop files here to upload')}</div>
                                    </div>
                                )}
                            </div>

                            {/* Uploaded Files Section (files added but not yet saved) */}
                            {uploadedFiles.length > 0 && (
                                <div className={styles.statusSection}>
                                    <div className={styles.statusHeader}>
                                        <div className={styles.statusHeaderLeft}>
                                            <CheckCircle2 className={`${styles.statusIcon} ${styles.statusIconProcessing}`} />
                                            {uploadedFiles.length} {t('documents to be processed')}
                                        </div>
                                        <button className={styles.textBtn} onClick={() => setShowUploadedFiles(!showUploadedFiles)}>
                                            {showUploadedFiles ? t('Hide') : t('Show')}
                                        </button>
                                    </div>
                                    <div
                                        className={`${styles.statusFiles} ${!showUploadedFiles ? styles.statusFilesCollapsed : ''}`}
                                    >
                                        {uploadedFiles.map((fileName, index) => (
                                            <div key={index} className={styles.fileItem}>
                                                <div className={`${styles.fileItemIcon} ${styles.fileSuccessIcon}`}>
                                                    <CheckCircle2 size={16} />
                                                </div>
                                                <span className={styles.fileItemName}>{fileName}</span>
                                                <button
                                                    className={styles.fileDeleteBtn}
                                                    aria-label='Delete file'
                                                    onClick={() => onUploadDocumentRemove(fileName)}
                                                >
                                                    <Trash2 size={16} />
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Processing Status Section */}
                            {allProcessingFiles.length > 0 && (
                                <div className={styles.statusSection}>
                                    <div className={styles.statusHeader}>
                                        <div className={styles.statusHeaderLeft}>
                                            <CheckCircle2 className={`${styles.statusIcon} ${styles.statusIconProcessing}`} />
                                            {allProcessingFiles.length} {t('documents are being processed')}
                                        </div>
                                        <button className={styles.textBtn} onClick={() => setShowProcessingFiles(!showProcessingFiles)}>
                                            {showProcessingFiles ? t('Hide') : t('Show')}
                                        </button>
                                    </div>
                                    <div
                                        className={`${styles.statusFiles} ${!showProcessingFiles ? styles.statusFilesCollapsed : ''}`}
                                    >
                                        {allProcessingFiles.map((file, index) => (
                                            <div key={index} className={styles.fileItem}>
                                                {/* Spinner for each file */}
                                                <img
                                                    src={spinnerGif}
                                                    alt='Processing'
                                                    style={{ width: 20, height: 20, marginRight: 8, verticalAlign: 'middle' }}
                                                />
                                                <span className={styles.fileItemName}>{file.item1}</span>
                                                <button
                                                    className={styles.fileDeleteBtn}
                                                    aria-label='Delete file'
                                                    onClick={() => onUploadDocumentRemove(file.item1!)}
                                                >
                                                    <Trash2 size={16} />
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Success Status Section */}
                            {completeFiles.length > 0 && (
                                <div className={styles.statusSection}>
                                    <div className={styles.statusHeader}>
                                        <div className={styles.statusHeaderLeft}>
                                            <CheckCircle2 className={`${styles.statusIcon} ${styles.statusIconSuccess}`} />
                                            {completeFiles.length} {t('Documents have successfully been processed')}
                                        </div>
                                        <button className={styles.textBtn} onClick={() => setShowSuccessFiles(!showSuccessFiles)}>
                                            {showSuccessFiles ? t('Hide') : t('Show')}
                                        </button>
                                    </div>
                                    <div
                                        className={`${styles.statusFiles} ${!showSuccessFiles ? styles.statusFilesCollapsed : ''}`}
                                    >
                                        {completeFiles.map((file, index) => (
                                            <div key={index} className={styles.fileItem}>
                                                <div className={`${styles.fileItemIcon} ${styles.fileSuccessIcon}`}>
                                                    <CheckCircle2 size={16} />
                                                </div>
                                                <span className={styles.fileItemName}>{file.item1}</span>
                                                <button
                                                    className={styles.fileDeleteBtn}
                                                    aria-label='Delete file'
                                                    onClick={() => onUploadDocumentRemove(file.item1!)}
                                                >
                                                    <Trash2 size={16} />
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Partial Processing Status Section */}
                            {partialFiles.length > 0 && (
                                <div className={styles.statusSection}>
                                    <div className={styles.statusHeader}>
                                        <div className={styles.statusHeaderLeft}>
                                            <AlertTriangle className={`${styles.statusIcon} ${styles.statusIconWarning}`} />
                                            {partialFiles.length} {t('documents have been partially processed.')}
                                        </div>
                                        <div className={styles.statusHeaderRight}>
                                            <button
                                                className={styles.textBtn}
                                                onClick={() => setShowPartialFiles(!showPartialFiles)}
                                            >
                                                {showPartialFiles ? t('Hide') : t('Show')}
                                            </button>
                                            <Tooltip
                                                content={
                                                    <ul className={styles.partialFileProcessingTooltip}>
                                                        <li>
                                                            {t(
                                                                'personalGpt.knowledgeBase.partialFileProcessingTooltip.AcceptedFileFormats',
                                                            )}
                                                        </li>
                                                        <li>
                                                            {t(
                                                                'personalGpt.knowledgeBase.partialFileProcessingTooltip.UnsupportedFileFormats',
                                                            )}
                                                        </li>
                                                        <li>
                                                            {t(
                                                                'personalGpt.knowledgeBase.partialFileProcessingTooltip.ContentRecommendations',
                                                            )}
                                                        </li>
                                                    </ul>
                                                }
                                                positioning='below'
                                                relationship='description'
                                                withArrow
                                            >
                                                <button className={styles.iconBtn} aria-label='More information'>
                                                    <Info size={16} />
                                                </button>
                                            </Tooltip>
                                        </div>
                                    </div>

                                    <div
                                        className={`${styles.statusFiles} ${!showPartialFiles ? styles.statusFilesCollapsed : ''}`}
                                    >
                                        {partialFiles.map((file, index) => (
                                            <div key={index} className={styles.fileItem}>
                                                <div className={`${styles.fileItemIcon} ${styles.fileWarningIcon}`}>
                                                    <AlertTriangle size={16} />
                                                </div>
                                                <span className={styles.fileItemName}>{file.item1}</span>
                                                <button
                                                    className={styles.fileDeleteBtn}
                                                    aria-label='Delete file'
                                                    onClick={() => onUploadDocumentRemove(file.item1!)}
                                                >
                                                    <Trash2 size={16} />
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Failed Processing Status Section */}
                            {failedFiles.length > 0 && (
                                <div className={styles.statusSection}>
                                    <div className={styles.statusHeader}>
                                        <div className={styles.statusHeaderLeft}>
                                            <AlertTriangle className={`${styles.statusIcon} ${styles.statusIconDanger}`} />
                                            {failedFiles.length} {t('documents failed to process.')}
                                        </div>
                                        <div className={styles.statusHeaderRight}>
                                            <button
                                                className={styles.textBtn}
                                                onClick={() => setShowFailedFiles(!showFailedFiles)}
                                            >
                                                {showFailedFiles ? t('Hide') : t('Show')}
                                            </button>
                                            <button className={styles.iconBtn} aria-label='More information'>
                                                <Info size={16} />
                                            </button>
                                        </div>
                                    </div>
                                    <div
                                        className={`${styles.statusFiles} ${!showFailedFiles ? styles.statusFilesCollapsed : ''}`}
                                    >
                                        {failedFiles.map((file, index) => (
                                            <div key={index} className={styles.fileItem}>
                                                <div className={`${styles.fileItemIcon} ${styles.fileDangerIcon}`}>
                                                    <AlertTriangle size={16} />
                                                </div>
                                                <span className={styles.fileItemName}>{file.item1}</span>
                                                <button
                                                    className={styles.fileDeleteBtn}
                                                    aria-label='Delete file'
                                                    onClick={() => onUploadDocumentRemove(file.item1!)}
                                                >
                                                    <Trash2 size={16} />
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Attachments/tags UI removed as requested */}
                        </section>
                    )}
                </div>
            )}

            <CDialog
                open={showConfirmDialog}
                title={t('adminSettings.survey.leavePage.title')}
                primaryActionText={t('adminSettings.survey.leavePage.confirmButton')}
                secondaryActionText={t('adminSettings.survey.leavePage.cancelButton')}
                onPrimaryAction={handleConfirm}
                onSecondaryAction={handleCancel}
            >
                <div>{t('adminSettings.survey.leavePage.message')}</div>
            </CDialog>
        </div>
    );
};

export default ConfigureGptView;
